<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui'
import { computed } from 'vue'

import EButton from './EButton.vue'
import EIcon from './EIcon.vue'
import { useUiStrings } from '../lib/strings'

/**
 * Modal dialog and slide-over drawer, same component.
 *
 * CLAUDE.md §11 wants quick-create to open "inline drawers — never lose page
 * context", which is the `drawer` variant: it slides in from the inline end,
 * keeps the page visible behind a scrim, and returns focus to the trigger on
 * close. The modal variant is for confirmations and short forms.
 *
 * Focus trapping, scroll locking, `Escape`, outside-click and the
 * `aria-labelledby`/`aria-describedby` wiring all come from reka-ui's Dialog.
 */

const props = withDefaults(
  defineProps<{
    title: string
    description?: string
    variant?: 'modal' | 'drawer'
    size?: 'sm' | 'md' | 'lg' | 'xl'
    /** Suppress the built-in close button when the flow must be completed. */
    hideClose?: boolean
    /**
     * Block dismissal by scrim click and Escape. Use only where dismissing
     * would lose unsaved work; always leave an explicit Cancel in the footer.
     */
    persistent?: boolean
    closeLabel?: string
  }>(),
  { description: '', variant: 'modal', size: 'md', closeLabel: '' },
)

const emit = defineEmits<{ close: [] }>()

const open = defineModel<boolean>('open', { default: false })

const strings = useUiStrings()

const resolvedCloseLabel = computed(() =>
  props.closeLabel === '' ? strings.value.dialogClose : props.closeLabel,
)

const contentClasses = computed(() => [
  'e-dialog__content',
  `e-dialog__content--${props.variant}`,
  `e-dialog__content--${props.size}`,
])

function onOpenChange(value: boolean): void {
  open.value = value
  if (!value) emit('close')
}

function guard(event: Event): void {
  if (props.persistent) event.preventDefault()
}
</script>

<template>
  <DialogRoot :open="open" @update:open="onOpenChange">
    <DialogPortal>
      <DialogOverlay class="e-dialog__overlay" />
      <DialogContent
        :class="contentClasses"
        @escape-key-down="guard"
        @pointer-down-outside="guard"
        @interact-outside="guard"
      >
        <header class="e-dialog__header">
          <div class="e-dialog__heading">
            <DialogTitle class="e-dialog__title">{{ title }}</DialogTitle>
            <DialogDescription v-if="description !== ''" class="e-dialog__description">
              {{ description }}
            </DialogDescription>
          </div>

          <DialogClose v-if="!hideClose" as-child>
            <EButton variant="ghost" size="sm" icon-only :aria-label="resolvedCloseLabel">
              <template #icon><EIcon name="close" :size="16" /></template>
            </EButton>
          </DialogClose>
        </header>

        <div class="e-dialog__body">
          <slot />
        </div>

        <footer v-if="$slots.footer" class="e-dialog__footer">
          <slot name="footer" />
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style>
.e-dialog__overlay {
  position: fixed;
  inset: 0;
  z-index: var(--erpx-z-overlay);
  background-color: var(--erpx-color-scrim);
  animation: e-fade-in var(--erpx-duration-normal) var(--erpx-easing-standard);
}

.e-dialog__content {
  position: fixed;
  z-index: var(--erpx-z-dialog);
  display: flex;
  flex-direction: column;
  background-color: var(--erpx-color-surface-raised);
  color: var(--erpx-color-text-primary);
  box-shadow: var(--erpx-shadow-overlay);
}

.e-dialog__content:focus-visible {
  outline: none;
}

/* modal */
.e-dialog__content--modal {
  inset-block-start: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
  width: calc(100vw - var(--erpx-space-8));
  max-height: calc(100vh - var(--erpx-space-16));
  border: 1px solid var(--erpx-color-border-subtle);
  border-radius: var(--erpx-radius-lg);
  animation: e-dialog-in var(--erpx-duration-normal) var(--erpx-easing-standard);
}

[dir='rtl'] .e-dialog__content--modal {
  transform: translate(50%, -50%);
}

.e-dialog__content--modal.e-dialog__content--sm { max-width: 24rem; }
.e-dialog__content--modal.e-dialog__content--md { max-width: 34rem; }
.e-dialog__content--modal.e-dialog__content--lg { max-width: 48rem; }
.e-dialog__content--modal.e-dialog__content--xl { max-width: 64rem; }

/* drawer — the page stays visible and in place behind it */
.e-dialog__content--drawer {
  inset-block: 0;
  inset-inline-end: 0;
  width: 100%;
  border-inline-start: 1px solid var(--erpx-color-border-subtle);
  animation: e-drawer-in var(--erpx-duration-normal) var(--erpx-easing-standard);
}

.e-dialog__content--drawer.e-dialog__content--sm { max-width: 22rem; }
.e-dialog__content--drawer.e-dialog__content--md { max-width: 32rem; }
.e-dialog__content--drawer.e-dialog__content--lg { max-width: 46rem; }
.e-dialog__content--drawer.e-dialog__content--xl { max-width: 62rem; }

.e-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--erpx-space-3);
  padding: var(--erpx-space-4);
  border-block-end: 1px solid var(--erpx-color-border-subtle);
}

.e-dialog__heading {
  display: flex;
  flex-direction: column;
  gap: var(--erpx-space-1);
  min-width: 0;
}

.e-dialog__title {
  margin: 0;
  font-size: var(--erpx-text-lg);
  font-weight: var(--erpx-weight-semibold);
  line-height: var(--erpx-leading-tight);
  letter-spacing: var(--erpx-tracking-tight);
}

.e-dialog__description {
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-sm);
  line-height: var(--erpx-leading-snug);
}

.e-dialog__body {
  flex: 1;
  min-height: 0;
  padding: var(--erpx-space-4);
  overflow-y: auto;
}

.e-dialog__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--erpx-space-2);
  padding: var(--erpx-space-3) var(--erpx-space-4);
  border-block-start: 1px solid var(--erpx-color-border-subtle);
  background-color: var(--erpx-color-surface-sunken);
}

@keyframes e-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes e-dialog-in {
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.98); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

@keyframes e-drawer-in {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

[dir='rtl'] .e-dialog__content--drawer {
  animation-name: e-drawer-in-rtl;
}

@keyframes e-drawer-in-rtl {
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
}

@media (prefers-reduced-motion: reduce) {
  .e-dialog__overlay,
  .e-dialog__content {
    animation: none;
  }
}
</style>
